import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { gql, useMutation, useQuery } from "@apollo/client";
import { init as initPptxPreview } from "pptx-preview";
import { Document, Page, pdfjs } from "react-pdf";
import pdfWorker from "pdfjs-dist/build/pdf.worker.min.js";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker as string;

const GET_USER_COMPLIANCE_STATUS = gql`
  query GetUserComplianceStatus {
    getUserComplianceStatus {
      item
      name
      pdf
    }
  }
`;

const RECORD_USER_COMPLIANCE_ACTION = gql`
  mutation RecordUserComplianceAction($action: AddComplianceActionInput!) {
    recordUserComplianceAction(action: $action) {
      message
      responseCode
    }
  }
`;

const PPT_RATIO = 16 / 9;
const MIN_WIDTH = 640;
const MIN_HEIGHT = 360;

const computeViewerSize = () => {
  if (typeof window === "undefined") {
    return { width: 1280, height: 720 };
  }
  const targetHeight = Math.max(MIN_HEIGHT, Math.round(window.innerHeight * 0.8));
  const rawWidth = Math.round(targetHeight * PPT_RATIO);
  const maxWidth = Math.max(MIN_WIDTH, Math.round(window.innerWidth * 0.98));
  const width = Math.min(rawWidth, maxWidth);
  return { width, height: targetHeight };
};

type TrainingProps = {
  item?: string;
  onDone?: () => void | Promise<void>;
  embedded?: boolean;
};

export const Training: React.FC<TrainingProps> = ({ item, onDone, embedded }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [recordCompliance] = useMutation(RECORD_USER_COMPLIANCE_ACTION);

  const [slides, setSlides] = useState<HTMLElement[]>([]);
  const [slideCount, setSlideCount] = useState<number>(0);
  const [current, setCurrent] = useState<number>(0);
  const [visitedSlides, setVisitedSlides] = useState<Set<number>>(() => new Set());
  const [pptxError, setPptxError] = useState<string>("");
  const [completionError, setCompletionError] = useState<string>("");
  const [recordingCompletion, setRecordingCompletion] = useState(false);
  const [recordedCompletionItem, setRecordedCompletionItem] = useState<string | null>(null);
  const [viewerSize, setViewerSize] = useState<{ width: number; height: number }>({ width: 1280, height: 720 });
  const [baseSize, setBaseSize] = useState<{ width: number; height: number } | null>(null);
  const viewerRef = useRef<HTMLDivElement | null>(null);
  const recordedCompletionItemRef = useRef<string | null>(null);
  const completionRequestRef = useRef<Promise<boolean> | null>(null);

  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pdfVisited, setPdfVisited] = useState<Set<number>>(() => new Set());
  const [pdfError, setPdfError] = useState<string>("");
  const [pdfScale] = useState<number>(1);

  const itemKey = useMemo(() => {
    const raw = item || params.item || location.pathname.split("/").pop() || "";
    const decoded = decodeURIComponent(raw).trim();
    return decoded || "training";
  }, [item, params.item, location.pathname]);

  const { data, loading } = useQuery(GET_USER_COMPLIANCE_STATUS, {
    fetchPolicy: "network-only",
  });

  const matchingItem = useMemo(() => {
    const list = data?.getUserComplianceStatus || [];
    const key = itemKey.toLowerCase();
    return list.find((entry: any) => {
      const itemMatch = (entry?.item || "").toLowerCase() === key;
      const nameMatch = (entry?.name || "").toLowerCase() === key;
      return itemMatch || nameMatch;
    });
  }, [data, itemKey]);

  const trainingName = matchingItem?.name || matchingItem?.item || itemKey || "Training";
  const resourcePath = matchingItem?.pdf ? `${process.env.PUBLIC_URL || ""}/docs/${matchingItem.pdf}` : null;
  const isPdf = resourcePath ? resourcePath.toLowerCase().endsWith(".pdf") : false;
  const completionItem = matchingItem?.item || itemKey || trainingName;

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const updateSize = () => {
      const size = computeViewerSize();
      setViewerSize(size);
      setBaseSize((prev) => prev ?? size);
    };
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => {
      window.removeEventListener("resize", updateSize);
    };
  }, []);

  useEffect(() => {
    if (isPdf) {
      return;
    }
    if (!baseSize) {
      return;
    }
    if (typeof document === "undefined") {
      return;
    }

    let cancelled = false;
    let previewer: any = null;
    let tempHost: HTMLDivElement | null = null;

    const loadSlides = async () => {
      if (!resourcePath) {
        setPptxError("No training file configured for this item.");
        return;
      }
      try {
        const res = await fetch(resourcePath, { cache: "no-cache" });
        if (!res.ok) {
          setPptxError(`Could not load PPTX (${res.status})`);
          return;
        }
        const buf = await res.arrayBuffer();

        tempHost = document.createElement("div");
        tempHost.style.position = "absolute";
        tempHost.style.left = "-9999px";
        tempHost.style.top = "-9999px";
        tempHost.style.width = `${baseSize.width}px`;
        tempHost.style.height = `${baseSize.height}px`;
        document.body.appendChild(tempHost);

        const originalKeys = Object.keys;
        Object.keys = function(obj: any): string[] {
          if (obj === undefined || obj === null) {
            return [];
          }
          return originalKeys(obj);
        } as any;

        try {
          previewer = initPptxPreview(tempHost, { mode: "list", width: baseSize.width, height: baseSize.height });
          await previewer.preview(buf);
        } finally {
          Object.keys = originalKeys;
        }

        if (cancelled) {
          return;
        }

        const renderedSlides = Array.from(
          tempHost.querySelectorAll<HTMLElement>(".pptx-preview-slide-wrapper")
        )
          .sort((a, b) => {
            const indexA = a.getAttribute("data-slide-index");
            const indexB = b.getAttribute("data-slide-index");
            if (indexA !== null && indexB !== null) {
              return parseInt(indexA, 10) - parseInt(indexB, 10);
            }
            const parent = a.parentElement;
            if (parent) {
              const children = Array.from(parent.children);
              return children.indexOf(a) - children.indexOf(b);
            }
            return 0;
          })
          .map((node) => node.cloneNode(true) as HTMLElement);

        setSlides(renderedSlides);
        setSlideCount(renderedSlides.length);
        setCurrent(0);
        setVisitedSlides(new Set(renderedSlides.length ? [0] : []));
        setPptxError("");
      } catch (err: any) {
        if (!cancelled) {
          setPptxError(err?.message || "Failed to render PPTX");
        }
      } finally {
        if (previewer?.destroy) {
          previewer.destroy();
        }
        if (tempHost?.parentNode) {
          tempHost.parentNode.removeChild(tempHost);
        }
      }
    };

    loadSlides();

    return () => {
      cancelled = true;
      if (previewer?.destroy) {
        previewer.destroy();
      }
      if (tempHost?.parentNode) {
        tempHost.parentNode.removeChild(tempHost);
      }
    };
  }, [baseSize, isPdf, resourcePath]);

  useEffect(() => {
    if (isPdf) {
      return;
    }
    const host = viewerRef.current;
    if (!host) {
      return;
    }
    host.innerHTML = "";
    const currentSlide = slides[current];
    if (!currentSlide) {
      return;
    }
    host.appendChild(currentSlide.cloneNode(true));
  }, [slides, current, viewerSize, isPdf]);

  const allPptxViewed = useMemo(() => {
    if (isPdf) {
      return false;
    }
    return slideCount > 0 ? visitedSlides.size >= slideCount : false;
  }, [slideCount, visitedSlides, isPdf]);

  const allPdfViewed = useMemo(() => {
    if (!isPdf) {
      return false;
    }
    return numPages > 0 ? pdfVisited.size >= numPages : false;
  }, [isPdf, numPages, pdfVisited]);

  const canComplete = resourcePath ? (isPdf ? allPdfViewed : allPptxViewed) : false;
  const completionRecorded = recordedCompletionItem === completionItem;

  useEffect(() => {
    recordedCompletionItemRef.current = null;
    completionRequestRef.current = null;
    setRecordedCompletionItem(null);
    setCompletionError("");
    setRecordingCompletion(false);
  }, [completionItem]);

  const recordCompletion = React.useCallback(async () => {
    if (!canComplete) {
      return false;
    }

    if (recordedCompletionItemRef.current === completionItem) {
      return true;
    }

    if (completionRequestRef.current) {
      return completionRequestRef.current;
    }

    setCompletionError("");
    setRecordingCompletion(true);

    const request = (async () => {
      try {
        const result = await recordCompliance({
          variables: {
            action: {
              item: completionItem,
            },
          },
        });
        const response = result.data?.recordUserComplianceAction;
        if (response?.responseCode && response.responseCode >= 400) {
          throw new Error(response.message || "Unable to record training completion.");
        }
        recordedCompletionItemRef.current = completionItem;
        setRecordedCompletionItem(completionItem);
        return true;
      } catch (err: any) {
        setCompletionError(err?.message || "Unable to record training completion.");
        return false;
      } finally {
        completionRequestRef.current = null;
        setRecordingCompletion(false);
      }
    })();

    completionRequestRef.current = request;
    return request;
  }, [canComplete, completionItem, recordCompliance]);

  useEffect(() => {
    if (!canComplete || recordedCompletionItemRef.current === completionItem) {
      return;
    }

    void recordCompletion();
  }, [canComplete, completionItem, recordCompletion]);

  const onPrev = () => {
    if (isPdf) {
      if (pageNumber <= 1) {
        return;
      }
      const next = pageNumber - 1;
      setPageNumber(next);
      return;
    }
    if (current <= 0) {
      return;
    }
    const nextIndex = current - 1;
    setCurrent(nextIndex);
    setVisitedSlides((prev) => {
      const next = new Set(prev);
      next.add(nextIndex);
      return next;
    });
  };

  const onNext = () => {
    if (isPdf) {
      if (numPages > 0 && pageNumber >= numPages) {
        return;
      }
      const next = pageNumber + 1;
      setPageNumber(next);
      return;
    }
    if (current >= slideCount - 1) {
      return;
    }
    const nextIndex = current + 1;
    setCurrent(nextIndex);
    setVisitedSlides((prev) => {
      const next = new Set(prev);
      next.add(nextIndex);
      return next;
    });
  };

  const onContinue = async () => {
    if (!canComplete || recordingCompletion) {
      return;
    }

    const returnTo = (location.state as { returnTo?: string } | undefined)?.returnTo;
    const saved = await recordCompletion();
    if (!saved) {
      return;
    }
    if (embedded && onDone) {
      await onDone();
      return;
    }
    if (embedded) {
      return;
    }
    if (returnTo) {
      navigate(returnTo);
      return;
    }
    navigate("/support/training");
  };

  if (loading) {
    return <div className="p-4 text-sm text-gray-700">Loading training...</div>;
  }

  const containerClass = embedded
    ? "w-full text-gray-900"
    : "mx-auto w-full max-w-6xl px-4 py-3 text-gray-900";

  const viewerLimit = 1288;
  const viewerWidth = Math.max(640, Math.min(viewerLimit, Math.round(viewerSize.width)));
  const pdfWrapperClass = embedded ? "w-full" : "mx-auto";
  const pdfWrapperStyle = embedded ? { width: "100%", maxWidth: viewerWidth } : { width: viewerWidth };
  const pptWrapperClass = embedded ? "mb-2" : "flex justify-center mb-2";
  const pageViewStatus = isPdf ? (
    <>
      <span>{numPages ? `Page ${Math.min(pageNumber, numPages)} of ${numPages}` : "Loading pages..."}</span>
      <span>{pdfVisited.size} viewed</span>
      <span className={allPdfViewed ? "text-green-600 font-medium" : "text-gray-500"}>
        {completionRecorded ? "Completion saved" : allPdfViewed ? "All pages reviewed" : "Scroll through every page"}
      </span>
    </>
  ) : (
    <>
      <span>{slideCount ? `Slide ${Math.min(current + 1, slideCount)} of ${slideCount}` : "Loading slides..."}</span>
      <span>{`${visitedSlides.size} viewed`}</span>
      <span className={allPptxViewed ? "text-green-600 font-medium" : "text-gray-500"}>
        {completionRecorded ? "Completion saved" : allPptxViewed ? "All slides reviewed" : "Scroll through every slide"}
      </span>
    </>
  );
  const navigationControls = (
    <div className="flex items-center gap-2">
      <button
        type="button"
        className={`px-2 py-0.5 rounded border border-gray-300 ${isPdf ? (pageNumber <= 1 ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-50") : (current <= 0 ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-50")}`}
        onClick={onPrev}
        disabled={isPdf ? pageNumber <= 1 : current <= 0}
      >
        Previous
      </button>
      <button
        type="button"
        className={`px-2 py-0.5 rounded border border-gray-300 ${isPdf ? (numPages > 0 && pageNumber >= numPages ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-50") : (current >= slideCount - 1 ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-50")}`}
        onClick={onNext}
        disabled={isPdf ? (numPages > 0 && pageNumber >= numPages) : (current >= slideCount - 1)}
      >
        Next
      </button>
      <button
        type="button"
        className={`px-3 py-1 rounded text-white ${canComplete && !recordingCompletion ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-300 cursor-not-allowed"}`}
        onClick={onContinue}
        disabled={!canComplete || recordingCompletion}
      >
        {recordingCompletion ? "Saving..." : "Continue"}
      </button>
    </div>
  );
  const navigationBar = (
    <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-gray-600">
      <div className="flex items-center gap-3" data-testid="training-progress">{pageViewStatus}</div>
      {navigationControls}
    </div>
  );

  return (
    <div className={containerClass}>
      {embedded ? null : (
        <div className="flex items-start justify-between mb-3">
          <h1 className="text-xl font-semibold text-gray-900">{trainingName}</h1>
        </div>
      )}
      <div className="mb-3">{navigationBar}</div>
      {completionError ? (
        <div className="mb-3 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700" data-testid="training-completion-error">
          {completionError}
        </div>
      ) : null}

      {!resourcePath ? (
        <div className="p-4 text-sm text-red-700 border border-red-200 bg-red-50 rounded">
          No training file configured for this item.
        </div>
      ) : isPdf ? (
        <div className={pdfWrapperClass} style={pdfWrapperStyle}>
          {pdfError ? (
            <div className="rounded border border-red-300 bg-red-50 text-red-700 p-3 mb-3 text-sm text-left">
              {pdfError}
              <div className="mt-2 text-xs text-gray-700">
                You can download the PDF directly:
                <a className="ml-2 underline text-blue-700" href={resourcePath} target="_blank" rel="noreferrer">Download PDF</a>
              </div>
            </div>
          ) : null}
          <div className="rounded border border-gray-200 bg-white shadow-sm overflow-hidden" style={{ width: "100%", maxWidth: viewerWidth }}>
            <Document
              file={resourcePath}
              onLoadSuccess={({ numPages }) => {
                setNumPages(numPages);
                setPageNumber(1);
                setPdfVisited(new Set(numPages ? [1] : []));
                setPdfError("");
              }}
              onLoadError={(err: any) => setPdfError(err?.message || "Failed to load PDF")}
              loading={<div className="p-4 text-center text-sm text-gray-600">Loading PDF...</div>}
              error={<div className="p-4 text-center text-sm text-red-700">Failed to load PDF.</div>}
            >
              <Page
                pageNumber={pageNumber}
                width={Math.round(viewerWidth * pdfScale)}
                onRenderSuccess={() => {
                  setPdfVisited((prev) => {
                    const next = new Set(prev);
                    next.add(pageNumber);
                    return next;
                  });
                }}
              />
            </Document>
          </div>
        </div>
      ) : (
        <>
          {pptxError ? (
            <div className="rounded border border-red-300 bg-red-50 text-red-700 p-3 mb-3 text-sm text-left">
              {pptxError}
              <div className="mt-2 text-xs text-gray-700">
                You can download the file directly:
                <a className="ml-2 underline text-blue-700" href={resourcePath} target="_blank" rel="noreferrer">Download</a>
              </div>
            </div>
          ) : null}
          <div className={pptWrapperClass}>
            <div
              ref={viewerRef}
              className="rounded border border-gray-200 bg-white shadow-sm"
              style={{ width: "100%", maxWidth: viewerWidth, height: viewerSize.height }}
            />
          </div>
        </>
      )}

    </div>
  );
};
